import type { CityConfig } from "../city-template/types";

export const salinasConfig: CityConfig = {
  city: "Salinas",
  state: "California",
  stateAbbr: "CA",
  slug: "salinas",
  route: "/cities/salinas",
  title: "Emergency Passport Salinas, CA | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Salinas, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Monterey County and Central Coast travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Salinas, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Salinas and Monterey County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Salinas%20California%20agricultural%20valley%20at%20golden%20hour%20with%20lush%20green%20fields%20and%20rolling%20hills%2C%20Steinbeck%20countryside%20landscape%2C%20Santa%20Lucia%20Mountains%20in%20the%20background%2C%20warm%20sunset%20light%20with%20amber%20and%20green%20tones%2C%20Central%20Coast%20farmland%20scenery%2C%20professional%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20and%20pastoral%20rural%20scene&width=1800&height=700&seq=salinas-hero-001&orientation=landscape",
  heroImageAlt: "Salinas California agricultural valley at golden hour with lush green fields and Santa Lucia Mountains",
  geoPosition: "36.6777;-121.6555",
  geoPlacename: "Salinas, California",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "450 Golden Gate Ave, San Francisco, CA 94102",
    distance: "Located in downtown San Francisco — approximately 75–90 minutes from Salinas via US-101",
  },
  nearbyCitiesZips: [
    { name: "Salinas", zips: "93901, 93905, 93906, 93907, 93908" },
    { name: "Monterey", zips: "93940, 93943, 93944" },
    { name: "Seaside", zips: "93955" },
    { name: "Marina", zips: "93933" },
    { name: "Castroville", zips: "95012" },
    { name: "Watsonville", zips: "95076, 95077" },
    { name: "Santa Cruz", zips: "95060, 95062, 95064" },
    { name: "Hollister", zips: "95023, 95024" },
    { name: "Gilroy", zips: "95020, 95021" },
    { name: "King City", zips: "93930" },
    { name: "Soledad", zips: "93960" },
    { name: "Gonzales", zips: "93926, 93927" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Monterey County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "San Francisco", route: "/cities/san-francisco" },
    { name: "San Jose", route: "/cities/san-jose" },
    { name: "Santa Barbara", route: "/cities/santa-barbara" },
    { name: "Ventura", route: "/cities/ventura" },
    { name: "Los Angeles", route: "/cities/los-angeles" },
    { name: "Fresno", route: "/cities/fresno" },
    { name: "Bakersfield", route: "/cities/bakersfield" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Salinas remotely via secure courier. We do not have a walk-in office in Salinas — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Monterey County travelers.",
  fpcOfficeDistance: "Serving all of Monterey County and the Central Coast",
  serviceAreaIntro: "From Old Town Salinas to the Creekbridge neighborhood, from North Salinas to the Alisal — Fast Passport Center serves the entire Salinas community. We regularly help travelers from Monterey, Seaside, Marina, and Watsonville get their passports fast.",
  nearbyCitiesIntro: "We serve all Salinas neighborhoods and nearby Central Coast cities including Monterey, Seaside, Marina, Castroville, Watsonville, and Santa Cruz. If you are in Monterey County or the surrounding area, we have you covered.",
  howItWorksIntro: "Most Salinas travelers complete everything online and ship documents overnight. No need to drive to San Francisco or wait in line at the regional agency.",
  mistakesIntro: "With Monterey Regional Airport serving the Central Coast and San Francisco International nearby, Salinas travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Salinas and Monterey County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Salinas? Here are the most common ones we hear from Monterey County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Monterey County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Maria G.",
      location: "Salinas, CA",
      rating: 5,
      text: "My trip to visit family in Mexico was in 2 days and my passport was expired. Fast Passport Center got me a new one in 30 hours. Saved our family reunion.",
      service: "Emergency Passport",
    },
    {
      name: "Thomas H.",
      location: "Monterey, CA",
      rating: 5,
      text: "Needed to renew for a cruise to Alaska. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Sarah L.",
      location: "Watsonville, CA",
      rating: 5,
      text: "Lost my passport a week before my study abroad trip to Spain. They walked me through the DS-64 process and got me a replacement in 48 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Michael R.",
      location: "Seaside, CA",
      rating: 5,
      text: "As a military family stationed at Fort Ord, we travel frequently. Fast Passport Center has been our go-to for years. Professional, fast, and reliable every time.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "salinas-1",
      name: "Salinas Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1000 W Alisal St",
      city: "Salinas",
      state: "CA",
      zip: "93901",
      phone: "(831) 424-4431",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Salinas",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "salinas-2",
      name: "Monterey Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1000 Lighthouse Ave",
      city: "Monterey",
      state: "CA",
      zip: "93940",
      phone: "(831) 649-4411",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "18.5 miles from Salinas",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "salinas-3",
      name: "Seaside Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1440 Del Monte Blvd",
      city: "Seaside",
      state: "CA",
      zip: "93955",
      phone: "(831) 899-2191",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "14.0 miles from Salinas",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for the Monterey Peninsula. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "salinas-4",
      name: "Watsonville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "221 Main St",
      city: "Watsonville",
      state: "CA",
      zip: "95076",
      phone: "(831) 724-4501",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "22.5 miles from Salinas",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for northern Monterey County and Santa Cruz County residents. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "salinas-5",
      name: "Marina Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3050 Del Monte Blvd",
      city: "Marina",
      state: "CA",
      zip: "93933",
      phone: "(831) 384-1501",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "12.0 miles from Salinas",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Monterey Peninsula residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "salinas-6",
      name: "Monterey County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "168 W Alisal St",
      city: "Salinas",
      state: "CA",
      zip: "93901",
      phone: "(831) 755-5041",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.3 miles from Salinas City Hall",
      website: "https://www.co.monterey.ca.us/government/departments-a-h/assessor-county-clerk-recorder",
      websiteLabel: "Monterey County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Central Coast photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Monterey County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Central Coast applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the San Francisco regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Monterey County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Central Coast travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Salinas." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Salinas address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Salinas before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Salinas for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Monterey County travelers." },
    { q: "How fast can I renew my passport in Salinas?", a: "Fast Passport Center offers fast passport renewal in Salinas in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my trip?", a: "If your passport expires before your trip, contact Fast Passport Center immediately. We can expedite your passport renewal in Salinas in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Salinas?", a: "Same-day passport appointments require a drive to the SF Regional Passport Agency. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Salinas residents." },
    { q: "Can I expedite a child passport in Salinas?", a: "Yes. Fast Passport Center expedites child passports in Salinas for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Salinas with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Salinas?",
      a: "With Fast Passport Center's emergency courier service, Salinas travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the San Francisco Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Salinas?",
      a: "Popular options include the Salinas Main Post Office (93901), Monterey Post Office (93940), Seaside Post Office (93955), and Monterey County Clerk (93901). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Salinas?",
      a: "Same-day passports are only available at the San Francisco Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in the Monterey Peninsula or nearby communities?",
      a: "We serve all of Monterey County and the Central Coast including Monterey, Seaside, Marina, Carmel, Pacific Grove, and Big Sur. Our overnight shipping labels work from any UPS location, so your location within the region doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Salinas?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Salinas?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Salinas and Monterey offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Salinas?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Salinas?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Monterey County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Salinas", address: "1600 N Main St, Salinas, CA 93906", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Monterey", address: "1200 Del Monte Ave, Monterey, CA 93940", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Salinas", address: "1040 S Main St, Salinas, CA 93901", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Watsonville", address: "1415 Main St, Watsonville, CA 95076", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Salinas", address: "1800 N Main St, Salinas, CA 93906", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Seaside", address: "1720 Fremont Blvd, Seaside, CA 93955", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Marina", address: "3050 Del Monte Blvd, Marina, CA 93933", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Salinas", address: "1335 N Davis Rd, Salinas, CA 93907", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Salinas",
      address: "1311 E Alisal St, Salinas, CA 93905",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Monterey",
      address: "1441 Canyon Del Rey Blvd, Seaside, CA 93955",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Watsonville",
      address: "1505 Main St, Watsonville, CA 95076",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Hollister",
      address: "1111 San Felipe Rd, Hollister, CA 95023",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for San Benito County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "MRY",
      name: "Monterey Regional Airport",
      distance: "15 miles from Salinas",
      address: "200 Fred Kane Dr, Monterey, CA 93940",
      note: "Regional airport serving the Monterey Peninsula with domestic flights and connections to international hubs.",
      flightUrl: "https://www.montereyairport.com/",
      flightLabel: "Monterey Regional",
      featured: true,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "85 miles from Salinas",
      address: "San Francisco, CA 94128",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via US-101.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Flight Info",
      featured: false,
    },
    {
      code: "SJC",
      name: "San Jose International Airport",
      distance: "55 miles from Salinas",
      address: "1701 Airport Blvd, San Jose, CA 95110",
      note: "Convenient for Silicon Valley and Central Coast travelers. Domestic flights with international connections.",
      flightUrl: "https://www.flysanjose.com/",
      flightLabel: "San Jose International",
      featured: false,
    },
  ],
};